const pickTimestamp = (...cands) => {
  for (const c of cands) {
    if (!c) continue;
    const d = new Date(c);
    if (!isNaN(d.getTime())) return d.toISOString();
  }
  return new Date().toISOString();
};

const createUserMessage = (formikValues) => {
  const nowIso = new Date().toISOString();

  const content = [];

  if (formikValues.Question?.trim()) {
    content.push({
      id: `question-${Date.now()}`,
      type: 'Message',
      value: formikValues.Question,
      dateCreated: nowIso,
    });
  }

  if (Array.isArray(formikValues.images)) {
    formikValues.images.forEach((img, index) => {
      content.push({
        id: `image-${index}-${Date.now()}`,
        type: 'Image',
        value: URL.createObjectURL(img.file),
        dateCreated: nowIso,
      });
    });
  }

  if (Array.isArray(formikValues.files)) {
    formikValues.files.forEach((file, index) => {
      content.push({
        id: `file-${index}-${Date.now()}`,
        type: 'Document',
        value: file.file.name,
        file: file.file,
        dateCreated: nowIso,
      });
    });
  }

  if (formikValues.audio) {
    content.push({
      id: `audio-${Date.now()}`,
      type: 'Audio',
      value: formikValues.audio.id,
      dateCreated: nowIso,
    });
  }

  return {
    role: 'user',
    content,
    dateCreated: nowIso,
    timestamp: nowIso,
  };
};

const transformChatData = (data) => {
  if (!data?.steps || !Array.isArray(data.steps)) return { messages: [] };

  const messageTs = pickTimestamp(
    data?.dateCreated,
    data?.createdAt,
    data?.timestamp,
    data?.DateCreated
  );

  const botChatDetailId = data?.botChatDetailId;

  const content = data.steps.flatMap((step) => {
    const stepTs = pickTimestamp(
      step?.dateCreated,
      step?.createdAt,
      step?.timestamp,
      data?.dateCreated,
      data?.createdAt,
      data?.timestamp,
      data?.DateCreated
    );

    switch (step.nodeType) {
      case 'Message':
        return [
          {
            id: step.id,
            type: 'Message',
            value: `<p>${step.message ?? ''}</p>`,
            isError: !!step.isError,
            stepId: botChatDetailId,
            dateCreated: stepTs,
          },
        ];
      case 'Image':
        return [
          {
            id: step.id,
            type: 'Image',
            value: step.imageUrl,
            stepId: botChatDetailId,
            dateCreated: stepTs,
          },
        ];
      case 'Video':
        return [
          {
            id: step.id,
            type: 'Video',
            value: step.videoUrl,
            stepId: botChatDetailId,
            dateCreated: stepTs,
          },
        ];
      case 'Document':
        return [
          {
            id: step.id,
            type: 'Document',
            value: step.documentUrl,
            name: step.name,
            stepId: botChatDetailId,
            dateCreated: stepTs,
          },
        ];
      case 'Question':
        return {
          id: step.id,
          type: 'Question',
          value: step.questions || [],
          visited: false,
          stepId: botChatDetailId,
          dateCreated: stepTs,
        };
      case 'Carousel':
        return {
          id: step.id,
          type: 'Carousel',
          value: step.carousel,
          stepId: botChatDetailId,
          dateCreated: stepTs,
        };
      case 'Form':
        return {
          id: step.id,
          type: 'Form',
          value: step.form,
          stepId: botChatDetailId,
          dateCreated: stepTs,
        };
      default:
        return [];
    }
  });

  return {
    ...data,
    messages: [
      {
        id: 'single-response',
        order: 1,
        role: 'bot',
        timestamp: messageTs,
        dateCreated: messageTs,
        isError: false,
        stepId: botChatDetailId,
        workflowName: data?.workflowName,
        content,
      },
    ],
  };
};

const transformNewChatData = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return { messages: [] };

  const mergedMessages = [];

  for (const chat of rows) {
    const isUser = !!chat.IsUserNotBot;

    const rawText =
      typeof chat?.NodeInfomation?.message === 'string'
        ? chat.NodeInfomation.message
        : typeof chat?.Text === 'string'
        ? chat.Text
        : '';

    if (isUser && rawText.trim() === '') {
      continue;
    }

    let value =
      chat.NodeInfomation?.message ??
      chat.NodeInfomation?.questions ??
      chat.NodeInfomation?.form ??
      chat.Text;

    if (
      chat.Type === 'Question' &&
      Array.isArray(chat.NodeInfomation?.questions)
    ) {
      value = chat.NodeInfomation.questions;
    }
    if (chat.Type === 'Form' && typeof chat.NodeInfomation?.form === 'object') {
      value = chat.NodeInfomation.form;
    }

    const stepTs = pickTimestamp(
      chat?.DateCreated,
      chat?.CreatedAt,
      chat?.Timestamp
    );

    const newContent = {
      id: chat.ChatDetailId,
      type: chat.Type,
      value,
      isError: chat.Value === false,
      stepId: chat.ActiveNodeId,
      dateCreated: stepTs,
    };

    const lastMessage = mergedMessages[mergedMessages.length - 1];

    if (lastMessage && lastMessage.role === (isUser ? 'user' : 'bot')) {
      lastMessage.content.push(newContent);
      lastMessage.dateCreated = pickTimestamp(lastMessage.dateCreated, stepTs);
      lastMessage.timestamp = lastMessage.dateCreated;
    } else {
      const msgTs = pickTimestamp(chat?.DateCreated, stepTs);
      mergedMessages.push({
        id: chat.ChatId,
        order: mergedMessages.length + 1,
        role: isUser ? 'user' : 'bot',
        timestamp: msgTs,
        dateCreated: msgTs,
        isError: chat.Value === false,
        stepId: chat.ActiveNodeId,
        workflowName: chat.WorkflowName || '',
        content: [newContent],
      });
    }
  }

  const messages = mergedMessages.filter(
    (m) => Array.isArray(m.content) && m.content.length > 0
  );

  return { messages };
};

function fixStreamUrl(streamUrl) {
  try {
    const u = new URL(streamUrl);
    const parts = u.pathname.split('/');

    const i = parts.indexOf('GuestChatStream');
    if (i !== -1 && parts.length > i + 2) {
      const qIdx = i + 2;
      const raw = parts[qIdx];
      const decoded = decodeURIComponent(raw.replace(/\+/g, ' '));
      parts[qIdx] = encodeURIComponent(decoded);
      u.pathname = parts.join('/');
    }
    return u.toString();
  } catch {
    return streamUrl;
  }
}

export {
  transformNewChatData,
  transformChatData,
  createUserMessage,
  fixStreamUrl,
};
